import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { ToastProvider } from '@/components/ui';
import { ScrollToTop } from '@/components/ScrollToTop';
import { BackHomeNav } from '@/components/BackHomeNav';
import { MembershipLifecycleBanner } from '@/components/MembershipLifecycleBanner';
import { CookieBanner } from '@/components/CookieBanner';
import { useSidebar } from '@/store/sidebar';

import { Sidebar } from './Sidebar';
import { Footer } from './Footer';

/** True cuando el viewport es desktop (lg+ = 1024px), donde la sidebar es fija. */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/**
 * AppShell — layout con NAVEGACIÓN LATERAL (Sprint 4 Wallet). La sidebar oscura
 * reemplaza al header superior; en desktop (lg+) es fija a la izquierda y el
 * contenido se desplaza con `lg:pl-64`. En mobile la sidebar se colapsa en una
 * top bar con hamburguesa + drawer (manejado dentro de `Sidebar`).
 */
export function AppShell(): JSX.Element {
  const collapsed = useSidebar((s) => s.collapsed);
  const isDesktop = useIsDesktop();
  // Desplazamiento del contenido = ancho de la sidebar SOLO en desktop (en mobile
  // la sidebar es una top bar y el contenido va full width). Inline para no
  // depender de utilidades responsive dinámicas del JIT de Tailwind.
  const paddingLeft = isDesktop ? (collapsed ? 64 : 256) : 0;
  return (
    <div className="min-h-screen bg-fc-bg">
      {/* ROUND10 #5 — scroll a tope en cada cambio de ruta. */}
      <ScrollToTop />
      <Sidebar />
      {/* Contenido desplazado por el ancho de la sidebar en desktop (según colapsado). */}
      <div
        style={{ paddingLeft }}
        className="flex min-h-screen flex-col transition-[padding] duration-200"
      >
        {/* ROUND12 — banner global de vencimiento de membresía (7d / expirada). */}
        <MembershipLifecycleBanner />
        <main id="main" className="relative flex-1">
          {/* ROUND10 #6 — Back / Inicio: overlay TRANSPARENTE sobre el tope del
              banner (dentro de `main` relative), en vez de un strip blanco arriba. */}
          <BackHomeNav />
          <Outlet />
        </main>
        <Footer />
      </div>
      {/* Banner global de cookies: fijo abajo, en cualquier ruta. Se corre el
          ancho de la barra para no taparle los botones de ingreso. */}
      <CookieBanner leftOffset={paddingLeft} />
      <ToastProvider />
    </div>
  );
}
